import { useState, useEffect, useCallback, useRef } from 'react';
import { sseService } from '../../../services/sseService';
import { MonitorSnapshot, CycleSummaryEvent } from '../../../types/monitor.types';

export function useMonitorData() {
  const [snapshot, setSnapshot] = useState<MonitorSnapshot | null>(null);
  const [cycleSummary, setCycleSummary] = useState<CycleSummaryEvent | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleUpdate = useCallback((data: MonitorSnapshot) => {
    setSnapshot(data);
    setIsConnected(true);
  }, []);

  const handleCycleSummary = useCallback((data: CycleSummaryEvent) => {
    setCycleSummary(data);
    // Clear previous auto-clear timer if a new cycle arrives
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    // Auto-clear after 15 seconds (bubbles fade, ticker returns to normal)
    clearTimerRef.current = setTimeout(() => setCycleSummary(null), 15_000);
  }, []);

  useEffect(() => {
    sseService.connectMonitorStream(handleUpdate, handleCycleSummary);

    checkIntervalRef.current = setInterval(() => {
      setIsConnected(sseService.isMonitorStreamConnected());
    }, 3000);

    return () => {
      sseService.disconnectMonitorStream();
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };
  }, [handleUpdate, handleCycleSummary]);

  return { snapshot, cycleSummary, isConnected };
}
